import { HighlightInit } from "@highlight-run/next/client";
import { ThemeProvider } from "@mui/material/styles";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { NextPage } from "next";
import type { AppProps } from "next/app";
import { ReactElement, ReactNode, useEffect, useState } from "react";
import { QueryClient, QueryClientProvider } from "react-query";
import { Provider } from "react-redux";
import "react-toastify/dist/ReactToastify.css";

import Layout from "../components/layout";
import LoaderPage from "../components/LoaderPage";
import PushNotificationLayout from "../components/PushNotificationLayout";
import AuthProvider from "../firebase/AuthProvider";
import { firebaseCloudMessaging } from "../firebase/firebase";
import { theme } from "../mui-customs/Theme";
import { store } from "../redux/store";
import "../styles/globals.css";

export type NextPageWithLayout<P = {}, IP = P> = NextPage<P, IP> & {
  getLayout?: (page: ReactElement) => ReactNode;
};

type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout;
};

function MyApp({ Component, pageProps }: AppPropsWithLayout) {
  const getLayout = Component.getLayout ?? ((page) => page);
  const [mounted, setMounted] = useState(false);
  if (mounted) {
    firebaseCloudMessaging.onMessage();
  }
  useEffect(() => {
    if ("serviceWorker" in navigator) {
      window.addEventListener("load", async function () {
        try {
          navigator.serviceWorker.register("/firebase-messaging-sw.js");
        } catch (error) {
          throw error;
        }
      });
    }
  }, []);
  useEffect(() => {
    const setToken = async () => {
      const token = await firebaseCloudMessaging.tokenInLocalStorage();
      if (token) {
        setMounted(true);
      }
    };
    setToken();
  }, []);
  const queryClient = new QueryClient();
  return (
    <>
      {process.env.NODE_ENV === "production" && (
        <HighlightInit
          projectId={"6gl53l9e"}
          serviceName="my-nextjs-frontend"
          tracingOrigins
          networkRecording={{
            enabled: true,
            recordHeadersAndBody: true,
            urlBlocklist: [],
          }}
        />
      )}
      <Provider store={store}>
        <QueryClientProvider client={queryClient}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <ThemeProvider theme={theme}>
              <AuthProvider>
                <PushNotificationLayout>
                  <Layout>
                    {getLayout(
                      <>
                        <LoaderPage />
                        <Component {...pageProps} />
                      </>
                    )}
                  </Layout>
                </PushNotificationLayout>
              </AuthProvider>
            </ThemeProvider>
          </LocalizationProvider>
        </QueryClientProvider>
      </Provider>
    </>
  );
}

export default MyApp;
